import React, { useCallback, useEffect, useState } from "react";
import { usePartnerFormStore } from "../store/usePartnerFormStore";
import { NavigationButtons } from "./ui";
import { getPublicPlans, type Plan } from "../apis/partnerForm";
import { calculatePayment } from "../utils/calculatePayment";

const PartnerFormStep3: React.FC = () => {
  const {
    coverageAreas,
    setCoveragePlan,
    previousStep,
    nextStep,
    errors,
    setError,
    clearError,
    updateListingDetails,
  } = usePartnerFormStore();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  const selectedPlan = coverageAreas.selectedPlan;

  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const response = await getPublicPlans();
        setPlans(response);
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Update listing amount when plan changes
  useEffect(() => {
    if (selectedPlan) {
      const amount = calculatePayment({
        selectedPlan: selectedPlan,
        selectedHexagons: coverageAreas.selectedHexagons,
      });
      updateListingDetails({ listingAmount: amount, totalServiceCharge: amount });
    }
  }, [selectedPlan, coverageAreas.selectedHexagons, updateListingDetails]);

  const handlePlanSelect = useCallback(
    (plan: Plan) => {
      setCoveragePlan(plan);
      clearError("coveragePlan");
    },
    [setCoveragePlan, clearError]
  );

  const validateStep = (): boolean => {
    if (!selectedPlan) {
      setError("coveragePlan", "Please select a coverage plan");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) nextStep();
  };

  // Helper function to safely format currency
  const formatCurrency = (value: number | undefined | null): string => {
    if (value === undefined || value === null || isNaN(value)) return "0";
    return value.toLocaleString();
  };

  // Calculate discount percentage
  const getDiscountPercentage = (originalPrice: number, sellingPrice: number): number => {
    if (originalPrice <= 0 || sellingPrice <= 0) return 0;
    return Math.round(((originalPrice - sellingPrice) / originalPrice) * 100);
  };

  if (isLoadingPlans) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-secondary-600">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5 md:space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-secondary-900 mb-2">
          Choose Your Coverage Plan
        </h2>
        <p className="text-sm md:text-base text-secondary-600">
          Select a plan that best fits your business needs
        </p>
      </div>

      {/* Plan Selection Cards */}
      <div>
        {errors.coveragePlan && (
          <p className="mb-2 text-sm text-red-600">{errors.coveragePlan as string}</p>
        )}

        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {plans.map((plan) => {
            const isActive = selectedPlan?.id === plan.id;
            const hasOffer = plan.offer?.isActive ?? false;
            const isCustom = plan.type === 'custom' || plan.name?.toLowerCase().includes('custom');
            const costPerHex = plan.costPerHex || plan.pricing?.costPerHex || 0;
            const originalPrice = plan.pricing?.originalPrice || 0;
            const sellingPrice = plan.pricing?.sellingPrice || 0;
            const payInitially = plan.pricing?.payInitially || 0;
            const payAfterSales = plan.pricing?.payAfterSales || 0;
            const discountPercent = getDiscountPercentage(originalPrice, sellingPrice);
            const hexCount = plan.features?.hexCount ?? 0;
            const coverageArea = plan.features?.coverageAreaKm2 ?? 0;
            const isTrial = plan.isTrial === "yes";
            const isAvailable = plan.availability?.isAvailable ?? true;
            const hasDiscount = originalPrice > sellingPrice && !isTrial && !isCustom;
            
            return (
              <button
                key={plan.id}
                type="button"
                onClick={() => isAvailable && handlePlanSelect(plan)}
                disabled={!isAvailable}
                className={`relative rounded-xl border-2 p-4 text-left transition-all duration-200 ${
                  isActive
                    ? "border-primary-500 bg-primary-50 ring-2 ring-primary-200 shadow-md"
                    : "border-secondary-200 bg-white hover:border-primary-300 hover:shadow-sm"
                } ${isTrial ? "border-amber-300 bg-amber-50/30" : ""} ${
                  isCustom ? "border-sky-300 bg-gradient-to-b from-white to-sky-50/20" : ""
                } ${
                  !isAvailable ? "opacity-50 cursor-not-allowed grayscale" : ""
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">
                    POPULAR
                  </div>
                )}
                
                {hasOffer && (
                  <div className="absolute -top-2 -left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">
                    {plan.offer?.value}% OFF
                  </div>
                )}
                
                {isActive && (
                  <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary-500 flex items-center justify-center">
                    <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                
                <p className="text-sm font-bold text-secondary-800">
                  {plan.displayName}
                  {isCustom && (
                    <span className="ml-1.5 text-[10px] font-semibold text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full border border-sky-200">
                      Custom Plan
                    </span>
                  )}
                  {isTrial && (
                    <span className="ml-1 text-xs text-amber-600 font-normal">(Trial)</span>
                  )}
                </p>
                
                <div className="mt-2">
                  <p className="text-2xl font-bold text-primary-600">
                    {isCustom ? "Custom" : (hexCount === 0 ? "∞" : hexCount)}
                  </p>
                  <p className="text-xs text-secondary-500 font-medium">
                    {isCustom ? "Custom Hexagons" : (hexCount === 0 ? "Full Kerala" : "hexagons")}
                  </p>
                  <p className="text-xs text-secondary-400">
                    {isCustom 
                      ? "Select any coverage on map"
                      : (hexCount === 0
                          ? "Entire State Coverage"
                          : `~${coverageArea} km² coverage`)}
                  </p>
                </div>
                
                <div className="mt-3 pt-3 border-t border-secondary-100">
                  {isCustom ? (
                    <>
                      <p className="text-base font-bold text-primary-600">
                        ₹{formatCurrency(costPerHex)}
                        <span className="font-normal text-secondary-500 text-xs ml-1">/ hex</span>
                      </p>
                      <p className="text-xs text-secondary-400">
                        excl. GST @ {plan.pricing?.gstPercentage || 18}%
                      </p>
                      <p className="text-[11px] text-primary-700 font-medium mt-1">
                        Pay for selected hexagons
                      </p>
                    </>
                  ) : payInitially > 0 ? (
                    <>
                      {hasDiscount && (
                        <p className="text-xs line-through text-secondary-400">
                          ₹{formatCurrency(originalPrice)}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-base font-bold text-primary-600">
                          ₹{formatCurrency(payInitially)}
                          <span className="font-normal text-secondary-400 text-xs ml-1">(Pay Initially)</span>
                        </p>
                        {hasDiscount && (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                            {discountPercent}% OFF
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-secondary-400">
                        excl. GST @ {plan.pricing?.gstPercentage || 0}%
                      </p>
                      {payAfterSales > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Pay ₹{formatCurrency(payAfterSales)} (ex. GST) After Sales
                        </p>
                      )}
                    </>
                  ) : isTrial ? (
                    <>
                      <p className="text-base font-bold text-amber-600">
                        ₹{formatCurrency(sellingPrice)}
                      </p>
                      <p className="text-xs text-secondary-400">
                        excl. GST @ {plan.pricing?.gstPercentage || 0}%
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Pay after first sale
                      </p>
                    </>
                  ) : hasDiscount ? (
                    <>
                      <p className="text-xs line-through text-secondary-400">
                        ₹{formatCurrency(originalPrice)}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-base font-bold text-primary-600">
                          ₹{formatCurrency(sellingPrice)}
                          <span className="font-normal text-secondary-400 text-xs">/yr</span>
                        </p>
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                          {discountPercent}% OFF
                        </span>
                      </div>
                      <p className="text-xs text-secondary-400">
                        excl. GST @ {plan.pricing?.gstPercentage || 0}%
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-base font-bold text-primary-600">
                        ₹{formatCurrency(sellingPrice)}
                        <span className="font-normal text-secondary-400 text-xs">/yr</span>
                      </p>
                      <p className="text-xs text-secondary-400">
                        excl. GST @ {plan.pricing?.gstPercentage || 0}%
                      </p>
                    </>
                  )}
                  {!isAvailable && plan.availability && (
                    <p className="text-xs text-red-500 mt-1">
                      Only {plan.availability.availableSlots} slots left
                    </p>
                  )}
                </div>

                {/* Feature highlights */}
                <div className="mt-3 pt-2 text-xs text-secondary-500">
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Up to {plan.features?.branchLimit || 1} branches</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{plan.features?.serviceCategories || 1} service category</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Info note */}
      {selectedPlan && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Next step:</strong> Set your branch location — you'll then be able to choose
            which nearby hexagons to include in your coverage area
            {selectedPlan.features?.hexCount ? ` (up to ${selectedPlan.features.hexCount} hexagons)` : ""}.
          </p>
        </div>
      )}

      <NavigationButtons
        onPrevious={previousStep}
        onNext={handleNext}
        nextLabel="Next: Branch Details"
      />
    </div>
  );
};

export default PartnerFormStep3;